import {
  useState,
  type FormEvent,
} from "react";
import {
  ArrowRight,
  Eye,
  EyeOff,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import {
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { consumeSessionDestination } from "@/lib/session-expiration";
import { AuthLayout } from "@/components/auth/AuthLayout";

import { usePlatformAuth } from "../hooks/usePlatformAuth";

type LocationState = {
  from?: string;
};

export function PlatformLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    login,
    isAuthenticated,
    isLoading,
  } = usePlatformAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] =
    useState(false);
  const [isSubmitting, setIsSubmitting] =
    useState(false);
  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);

  const locationState =
    location.state as LocationState | null;

  const destination =
    locationState?.from ?? "/";

  if (!isLoading && isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    const normalizedEmail =
      email.trim().toLowerCase();

    if (!normalizedEmail || !password) {
      setErrorMessage(
        "Enter your platform administrator email and password.",
      );

      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await login({
        email: normalizedEmail,
        password,
      });

      navigate(consumeSessionDestination(destination), {
        replace: true,
      });
    } catch (error) {
      const message =
        typeof error === "object" &&
        error !== null &&
        "response" in error
          ? "The email or password you entered is incorrect."
          : "Platform Console login is currently unavailable.";

      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout
      variant="platform"
      workspaceLabel="Platform Console"
      brandIcon={ShieldCheck}
      cardIcon={LockKeyhole}
      eyebrow="Platform Operations"
      heroTitle="Manage the MinistryIQ pilot from one secure workspace."
      heroDescription="Review pilot requests, provision churches, manage administrator invitations, and monitor onboarding."
      formTitle="Platform sign in"
      formDescription="Sign in with your MinistryIQ platform administrator account."
      footer="This area is restricted to authorized MinistryIQ platform personnel."
    >
      {errorMessage && (
        <div
          role="alert"
          className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"
        >
          <p className="text-sm font-medium text-rose-700">
            {errorMessage}
          </p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="mt-8 space-y-5"
      >
        <div>
          <label
            htmlFor="platform-email"
            className="text-sm font-semibold text-slate-700"
          >
            Email address
          </label>

          <input
            id="platform-email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setErrorMessage(null);
            }}
            placeholder="admin@example.com"
            disabled={isSubmitting}
            className="mt-2 h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100"
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-4">
            <label
              htmlFor="platform-password"
              className="text-sm font-semibold text-slate-700"
            >
              Password
            </label>

            <span className="text-xs font-medium text-slate-400">
              Platform administrators only
            </span>
          </div>

          <div className="relative mt-2">
            <input
              id="platform-password"
              type={
                showPassword
                  ? "text"
                  : "password"
              }
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setErrorMessage(null);
              }}
              disabled={isSubmitting}
              className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 pr-12 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100"
            />

            <button
              type="button"
              onClick={() =>
                setShowPassword(
                  (current) => !current,
                )
              }
              aria-label={
                showPassword
                  ? "Hide password"
                  : "Show password"
              }
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-400 transition hover:text-slate-700"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || isLoading}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              Signing in...
            </>
          ) : (
            <>
              Sign in to Platform Console
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}
